import { useEffect, useId, useMemo } from 'react'
import type { CSSProperties } from 'react'
import { currentLocale, subscribeLocale } from '../i18n/catalog'
import { getAdProvider } from '../ads/adProvider'
import type { AdSize, ResponsiveAdSizeRule } from '../ads/types'

export interface AdSlotProps {
  adUnitId: string
  sizes: readonly AdSize[]
  responsive?: readonly ResponsiveAdSizeRule[]
  position: string
  surface: string
  className?: string
  style?: CSSProperties
}

/**
 * Provider-neutral display-ad slot.
 *
 * Screen components provide only stable inventory/context values and the
 * application-selected locale. No member ID, song/share/file identifier,
 * lyrics, title, practice history, IP/device identifier or other user/content
 * data is forwarded for advertising targeting.
 */
export function AdSlot({
  adUnitId,
  sizes,
  responsive,
  position,
  surface,
  className,
  style,
}: AdSlotProps) {
  const reactId = useId()
  const elementId = useMemo(
    () => `miosegno-ad-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`,
    [reactId],
  )

  useEffect(() => {
    let cancelled = false
    let destroy = () => {}
    const provider = getAdProvider()

    void provider
      .configure({ appId: 'mioSegno', locale: currentLocale() })
      .then(() => provider.mountSlot({
        elementId,
        adUnitId,
        sizes,
        responsive,
        targeting: { position, surface },
        collapseWhenEmpty: true,
      }))
      .then((slot) => {
        if (cancelled) slot.destroy()
        else destroy = slot.destroy
      })
      .catch((error: unknown) => {
        // Development receives detailed diagnostics. Production must not expose
        // provider/internal diagnostics to ordinary users.
        if (import.meta.env.DEV) console.error('[mioSegno][ads] slot mount failed', error)
      })

    // Each mounted component can subscribe safely because the provider ignores
    // duplicate locale values and refreshes mounted slots only on an actual
    // application-locale change.
    const unsubscribe = subscribeLocale(() => provider.setLocale(currentLocale()))
    return () => {
      cancelled = true
      unsubscribe()
      destroy()
    }
  }, [adUnitId, elementId, position, responsive, sizes, surface])

  // Do not mark the provider host aria-hidden: commercial creatives can contain
  // focusable/clickable content rendered by the provider.
  return <div id={elementId} className={className} style={style} />
}

export default AdSlot
